import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  LinearProgress,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDialog } from "../../DialogContext";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { backend_api } from "../../../static/server";
import {
  generateUniqueMatterName,
  pageScrollStyle,
} from "../../../styles/styles";
import { LoadingButton } from "@mui/lab";
import {
  DocCategoryApiField,
  MatterApiField,
} from "../Settings/FieldsUi/InputFields";
import {
  createDocFolder,
  getAllDocFolder,
  getFolderById,
} from "../../../redux/Apps/Lawyerapp/DocumentSlice/DocumentSlice";
import axios from "axios";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";

const UploadFolderForm = ({ folderData, currentFolderData }) => {
  const { closeDialog } = useDialog();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [lenFilesInFolder, setLenFilesInFolder] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);
  const [fileUploadResults, setFileUploadResults] = useState([]);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [currentErrorFile, setCurrentErrorFile] = useState(null);
  const [uploadStopped, setUploadStopped] = useState(false);
  const userDecisionRef = useRef(null);
  const resolveDecisionRef = useRef(null);
  const { token } = useSelector((state) => state.user);
  const { loading } = useSelector((state) => state.document);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      folder_name: "",
      files: null,
      client: null,
      case: null,
      category: null,
    },
  });
  useEffect(() => {
    if (folderData) {
      setValue("folder_name", folderData?.name);
      setValue("files", folderData?.files, { shouldValidate: true });
      setLenFilesInFolder(Array.from(folderData?.files)?.length);
    }
  }, [folderData, setValue]);

  const uploadSingleFile = async (file, index, totalFiles) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", file?.name);

      const response = await axios.post(
        `${backend_api}documents/document/`,
        formData,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const progress = ((index + 1) / totalFiles) * 100;
      setUploadProgress(progress);
      setUploadStatus(
        `Uploading file ${index + 1} of ${totalFiles}: ${file.name}`
      );

      return {
        success: true,
        fileId: response.data.id || response.data,
        fileName: file.name,
        error: null,
      };
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      const errorMessage = error.response?.data?.message || error.message;

      return {
        success: false,
        fileId: null,
        fileName: file.name,
        error: errorMessage,
      };
    }
  };

  const handleSkipFile = () => {
    userDecisionRef.current = "skip";
    setShowErrorDialog(false);
    if (resolveDecisionRef.current) {
      resolveDecisionRef.current("skip");
      resolveDecisionRef.current = null;
    }
  };

  const handleStopUpload = () => {
    userDecisionRef.current = "stop";
    setUploadStopped(true);
    setShowErrorDialog(false);
    if (resolveDecisionRef.current) {
      resolveDecisionRef.current("stop");
      resolveDecisionRef.current = null;
    }
  };

  const waitForUserDecision = () => {
    return new Promise((resolve) => {
      userDecisionRef.current = null;
      resolveDecisionRef.current = resolve;
    });
  };

  const deleteUploadedFiles = async (fileIds) => {
    try {
      const deletePromises = fileIds.map(async (fileId) => {
        try {
          await axios.delete(`${backend_api}documents/document/${fileId}/`, {
            headers: {
              Authorization: `Token ${token}`,
            },
          });
          return { success: true, fileId };
        } catch (error) {
          console.error(`Failed to delete file ${fileId}:`, error);
          return { success: false, fileId, error: error.message };
        }
      });

      const results = await Promise.all(deletePromises);
      const successCount = results.filter((r) => r.success).length;
      const failedCount = results.filter((r) => !r.success).length;

      if (successCount > 0) {
        toast.info(`${successCount} uploaded files deleted successfully`);
      }
      if (failedCount > 0) {
        toast.warning(`Failed to delete ${failedCount} files`);
      }

      return results;
    } catch (error) {
      console.error("Error deleting files:", error);
      toast.error("Error occurred while deleting files");
    }
  };

  const uploadAllFiles = async (files) => {
    const fileArray = Array.from(files);
    const uploadedIds = [];
    const failed = [];
    const results = [];

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus("Starting file uploads...");
    setFailedFiles([]);
    setUploadedFileIds([]);
    setFileUploadResults([]);
    setUploadStopped(false);
    userDecisionRef.current = null;
    resolveDecisionRef.current = null;

    try {
      for (let i = 0; i < fileArray.length; i++) {
        if (uploadStopped) {
          setUploadStatus("Upload process stopped by user");
          break;
        }

        const file = fileArray[i];

        results[i] = { fileName: file.name, status: "uploading", error: null };
        setFileUploadResults([...results]);

        const result = await uploadSingleFile(file, i, fileArray.length);

        if (result.success) {
          uploadedIds.push(result.fileId);
          results[i] = { fileName: file.name, status: "success", error: null };
          setUploadedFileIds([...uploadedIds]);
        } else {
          setCurrentErrorFile({ file, result, index: i });
          setShowErrorDialog(true);

          // Small delay to ensure dialog is rendered
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Wait for user decision
          const decision = await waitForUserDecision();

          if (decision === "skip") {
            failed.push({ fileName: file.name, error: result.error });
            results[i] = {
              fileName: file.name,
              status: "skipped",
              error: result.error,
            };
            toast.warning(`Skipped file: ${file.name}`);
            // console.log(
            //   `File skipped: ${file.name}, continuing to next file...`
            // );
          } else if (decision === "stop") {
            setUploadStatus("Upload process stopped by user");
            results[i] = {
              fileName: file.name,
              status: "failed",
              error: result.error,
            };
            setFileUploadResults([...results]);
            // console.log("Upload process stopped by user");
            break;
          }
        }

        setFileUploadResults([...results]);
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const successCount = uploadedIds.length;
      const failedCount = failed.length;

      if (uploadStopped) {
        if (successCount > 0) {
          setUploadStatus(
            `Upload stopped. ${successCount} files uploaded before stopping.`
          );
        } else {
          setUploadStatus("Upload stopped. No files were uploaded.");
        }
      } else {
        if (successCount > 0) {
          setUploadStatus(
            `Upload completed! ${successCount} files uploaded successfully${
              failedCount > 0 ? `, ${failedCount} files skipped` : ""
            }`
          );
          toast.success(
            `${successCount} files uploaded successfully${
              failedCount > 0 ? `, ${failedCount} files skipped` : ""
            }`
          );
        } else {
          setUploadStatus("No files were uploaded successfully");
          toast.error("No files were uploaded successfully");
        }
      }

      setUploadedFileIds(uploadedIds);
      setFailedFiles(failed);
      return { uploadedIds, stopped: uploadStopped };
    } catch (error) {
      setUploadStatus(`Upload failed: ${error.message}`);
      toast.error(error.message);
      throw error;
    } finally {
      setIsUploading(false);
      setShowErrorDialog(false);
      setCurrentErrorFile(null);
      userDecisionRef.current = null;
      resolveDecisionRef.current = null;
    }
  };

  const createFolder = async (folderData, documentIds) => {
    try {
      if (documentIds.length === 0) {
        toast.error(
          "No files were uploaded successfully. Cannot create folder."
        );
        return;
      }

      const folderPayload = {
        folder_name: folderData.folder_name,
        case: folderData?.case?.id,
        client: folderData?.case?.case_related_person,
        category: folderData.category,
        documents: documentIds,
        ...(currentFolderData?.id && { parent: currentFolderData.id }),
      };

      const result = await dispatch(
        createDocFolder({
          url: `${backend_api}documents/document/folder/`,
          body: folderPayload,
          token: token,
        })
      ).unwrap();

      toast.success("Folder created successfully!");
      return result;
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error("Failed to create folder");
      throw error;
    }
  };

  const onSubmit = async (data) => {
    try {
      if (!data.files || data.files.length === 0) {
        toast.error("Please select files to upload");
        return;
      }

      const { uploadedIds, stopped } = await uploadAllFiles(data.files);

      if (stopped) {
        if (uploadedIds.length > 0) {
          const createFolderWithUploaded = window.confirm(
            `Upload was stopped. ${uploadedIds.length} files were uploaded successfully.\n\n` +
              `Choose OK to create folder with uploaded files, or Cancel to delete uploaded files and not create folder.`
          );

          if (createFolderWithUploaded) {
            await createFolder(data, uploadedIds);
            toast.success(`Folder created with ${uploadedIds.length} files`);
            closeDialog();
          } else {
            await deleteUploadedFiles(uploadedIds);
            toast.info("Uploaded files deleted. No folder created.");
          }
        } else {
          toast.info("No files were uploaded. Nothing to clean up.");
        }
      } else {
        if (uploadedIds.length > 0) {
          await createFolder(data, uploadedIds);
        } else {
          toast.error(
            "No files were uploaded successfully. Folder not created."
          );
        }
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      closeDialog();
      if (currentFolderData && currentFolderData?.id) {
        dispatch(
          getFolderById({
            url: `${backend_api}documents/document/folder/`,
            token: token,
            folderId: currentFolderData?.id,
          })
        );
      }
      dispatch(
        getAllDocFolder({
          url: `${backend_api}documents/document/folder/`,
          token: token,
          // teamMemberLawyerId: teamMemberLawyerId,
        })
      );
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircleIcon color="success" />;
      case "failed":
      case "skipped":
        return <ErrorIcon color="error" />;
      case "uploading":
        return <PendingIcon color="primary" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "success";
      case "skipped":
        return "warning";
      case "failed":
        return "error";
      case "uploading":
        return "primary";
      default:
        return "default";
    }
  };

  const isFormDisabled = isUploading || loading;

  return (
    <>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "1rem",
            }}
          >
            <Controller
              name="folder_name"
              control={control}
              rules={{ required: "Folder name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Folder Name"
                  size="small"
                  fullWidth
                  disabled={isFormDisabled}
                  error={Boolean(errors.folder_name)}
                  helperText={errors.folder_name?.message}
                />
              )}
            />

            <Controller
              name="case"
              control={control}
              rules={{ required: "Matter is required" }}
              render={({ field: { onChange, value } }) => (
                <MatterApiField
                  name="case"
                  label="Matter"
                  value={value}
                  onChange={onChange}
                  errors={errors}
                  token={token}
                  apiUrl={`${backend_api}case/register/`}
                  isRequired={true}
                  createMatterDialogTitle="Create New Matter"
                  noDataText="No matters found. Create one to get started!"
                  addButtonText="Add New Matter"
                  placeholder="Search and select matter"
                  generateUniqueMatterName={generateUniqueMatterName}
                  formatApiResponse={(data) => {
                    return data.res;
                  }}
                  AddMatterFunc={() => navigate("/dashboard/matter")}
                  returnFullData={true}
                />
              )}
            />

            <Controller
              name="category"
              control={control}
              rules={{ required: "Category is required" }}
              render={({ field: { onChange, value } }) => (
                <DocCategoryApiField
                  name="category"
                  label="Document Category"
                  value={value}
                  onChange={onChange}
                  errors={errors}
                  token={token}
                  apiUrl={`${backend_api}documents/category/`}
                  isRequired={true}
                  createCategoryDialogTitle="Create New Category"
                  noDataText="No categories found. Create one to get started!"
                  addButtonText="Add New Category"
                  placeholder="Search and select category"
                  formatApiResponse={(data) => {
                    return data.res;
                  }}
                />
              )}
            />

            {isUploading && (
              <Box sx={{ mt: "1rem" }}>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  {uploadStatus}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  {Math.round(uploadProgress)}% Complete
                </Typography>
              </Box>
            )}

            {/* File Upload Results */}
            {fileUploadResults.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  File Upload Status:
                </Typography>
                <List
                  dense
                  sx={{ maxHeight: 200, overflow: "auto", ...pageScrollStyle }}
                >
                  {fileUploadResults.map((result, index) => (
                    <ListItem key={index}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {getStatusIcon(result.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={result.fileName}
                        secondary={result.error}
                      />
                      <Chip
                        label={result.status}
                        size="small"
                        color={getStatusColor(result.status)}
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Upload Status Alert */}
            {uploadStatus && !isUploading && (
              <Alert
                severity={uploadedFileIds.length > 0 ? "success" : "error"}
                sx={{ mt: 1 }}
              >
                {uploadStatus}
                {failedFiles.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">
                      Skipped files:{" "}
                      {failedFiles.map((f) => f.fileName).join(", ")}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}
          </Box>

          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "0.5rem",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => closeDialog()}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <LoadingButton
              size="small"
              variant="contained"
              color="primary"
              type="submit"
              loading={isFormDisabled}
              disabled={isFormDisabled}
            >
              {isUploading
                ? `Uploading... (${Math.round(uploadProgress)}%)`
                : `Submit Folder with (${lenFilesInFolder}) file(s)`}
            </LoadingButton>
          </Box>
        </form>
      </Box>

      {/* Error Dialog */}
      <Dialog
        open={showErrorDialog}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>File Upload Error</DialogTitle>
        <DialogContent>
          {currentErrorFile && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Failed to upload file:{" "}
                <strong>{currentErrorFile.result.fileName}</strong>
              </Typography>
              <Typography variant="body2" color="error" gutterBottom>
                Error: {currentErrorFile.result.error}
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                What would you like to do?
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={handleStopUpload}
            color="error"
            variant="outlined"
          >
            Stop Upload Process
          </Button>
          <Button
            size="small"
            onClick={handleSkipFile}
            color="warning"
            variant="contained"
          >
            Skip This File & Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadFolderForm;
